// src/pages/Dashboard/Dashboard.js
import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';

const Dashboard = () => {
  const [selectedMetric, setSelectedMetric] = useState('calls');
  const [timeRange, setTimeRange] = useState('month');
  const [graphData, setGraphData] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [yAxisTicks, setYAxisTicks] = useState([]);

  // Campaign data definitions
  const campaigns = {
    all: {
      id: 'all',
      name: 'All Campaigns',
      metrics: {
        calls: {
          label: 'Number of calls',
          value: 228378,
          maxY: 15000
        },
        patchthrough: {
          label: 'Patch-through Calls',
          value: 1135,
          maxY: 100
        },
        persuaded: {
          label: 'Persuaded Voters',
          value: 31236,
          maxY: 2000
        },
        aiminutes: {
          label: 'AI minutes',
          value: 301345,
          maxY: 20000
        },
        sms: {
          label: 'SMS messages sent',
          value: 156750,
          maxY: 10000
        }
      }
    },
    oklahoma: {
      id: 'oklahoma',
      name: 'Oklahoma HB 3214',
      metrics: {
        calls: {
          label: 'Number of calls',
          value: 9632,
          maxY: 400
        },
        patchthrough: {
          label: 'Patch-through Calls',
          value: 1135,
          maxY: 100
        },
        persuaded: {
          label: 'Agreed',
          value: 1542,
          maxY: 100
        },
        aiminutes: {
          label: 'AI minutes',
          value: 5400,
          maxY: 400
        },
        sms: {
          label: 'SMS messages sent',
          value: 12850,
          maxY: 600
        }
      }
    },
    cruz: {
      id: 'cruz',
      name: 'Ted Cruz',
      metrics: {
        calls: {
          label: 'Number of calls',
          value: 218876,
          maxY: 15000
        },
        patchthrough: {
          label: 'Voting for Ted Cruz',
          value: 101245,
          maxY: 5000
        },
        persuaded: {
          label: 'Persuaded voters',
          value: 31236,
          maxY: 2000
        },
        aiminutes: {
          label: 'AI minutes',
          value: 278435,
          maxY: 20000
        },
        sms: {
          label: 'SMS messages sent',
          value: 143900,
          maxY: 8000
        }
      }
    }
  };

  // Define y-axis ranges for each metric based on campaign and actual data
  const getMetricYAxisRange = (metric, data) => {
    const campaign = getCurrentCampaign();
    
    // Get the maximum data point for this metric/campaign combination
    const dataMax = data && data.length ? Math.max(...data.map(item => item.value), 0) : 0;
    
    // Use a smaller maximum for better visualization
    const scaleFactor = 1.5; // Slightly higher than the data max
    return dataMax * scaleFactor;
  };

  // Get current campaign data
  const getCurrentCampaign = () => campaigns[selectedCampaign] || campaigns.all;

  // Define total values for each metric based on selected campaign
  const getMetricTotals = () => {
    const campaign = getCurrentCampaign();
    return {
      calls: campaign.metrics.calls.value,
      patchthrough: campaign.metrics.patchthrough.value,
      persuaded: campaign.metrics.persuaded.value,
      aiminutes: campaign.metrics.aiminutes.value,
      sms: campaign.metrics.sms.value
    };
  };

  // Get metric label based on selected campaign
  const getMetricLabel = (metric) => {
    const campaign = getCurrentCampaign();
    return campaign.metrics[metric]?.label || '';
  };

  // Get metric value based on selected campaign
  const getMetricValue = (metric) => {
    const campaign = getCurrentCampaign();
    return campaign.metrics[metric]?.value.toLocaleString() || '0';
  };

  // Generate data for the x-axis based on selected time period
  const generateDates = (range) => {
    const data = [];
    let pointsToGenerate = 30;
    
    if (range === 'week') pointsToGenerate = 6;
    if (range === 'yesterday') pointsToGenerate = 1;
    
    for (let i = 0; i <= pointsToGenerate; i++) {
      // For daily view, we won't show x-axis labels
      if (range === 'yesterday') {
        data.push("");
      } else {
        // Just use the numerical index as the label
        data.push(`${i}`);
      }
    }
    
    return data;
  };

  // Generate random data for each metric based on the time range and appropriate scale
  const generateData = (metric, range) => {
    const xAxisLabels = generateDates(range);
    const numPoints = xAxisLabels.length;
    const metricTotals = getMetricTotals();
    const total = metricTotals[metric] || 0;
    const avgPerPoint = total / numPoints;
    
    // Calculate reasonable min and max based on the average
    const minValue = avgPerPoint * 0.6;  // 60% of average as minimum
    const maxValue = avgPerPoint * 1.4;  // 140% of average as maximum
    
    // Create a more natural distribution pattern
    let dataPoints = [];
    
    // Different patterns based on metric for more realism
    if (metric === 'sms') {
      // SMS often has spikes on specific days (e.g., campaign blasts)
      dataPoints = xAxisLabels.map((_, i) => {
        // Create occasional spikes
        if (i % 7 === 2 || i % 9 === 0) {
          return minValue + (maxValue - minValue) * 0.9;
        } else {
          return minValue + (maxValue - minValue) * Math.random() * 0.7;
        }
      });
    } else {
      // Generate using smoothed random walks
      const smoothFactor = 0.3; // How much each point is influenced by previous point
      let previous = avgPerPoint;
      
      dataPoints = xAxisLabels.map(() => {
        // Get a random value with smoothing from previous point
        const randomValue = minValue + (maxValue - minValue) * Math.random();
        const value = previous * smoothFactor + randomValue * (1 - smoothFactor);
        previous = value;
        return Math.floor(value);
      });
    }
    
    // Calculate the sum of generated values
    const sum = dataPoints.reduce((a, b) => a + b, 0);
    
    // Adjust the values to match the total exactly
    const adjustmentFactor = total / sum;
    dataPoints = dataPoints.map(val => Math.floor(val * adjustmentFactor));
    
    // Ensure the sum is exactly the total by adding/subtracting the remainder from the last point
    const newSum = dataPoints.reduce((a, b) => a + b, 0);
    const remainder = total - newSum;
    dataPoints[dataPoints.length - 1] += remainder;
    
    return xAxisLabels.map((label, index) => {
      return {
        date: label,
        value: dataPoints[index]
      };
    });
  };

  // Generate nicely rounded ticks for the y-axis
  const generateYAxisTicks = (metric, data) => {
    if (!data || !data.length) return [0];
    
    // Find the max value in the data
    const dataMax = Math.max(...data.map(item => item.value), 0);
    
    // Calculate a reasonable number of ticks based on the data range
    const getTickInterval = (max) => {
      // Get order of magnitude
      const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
      
      // Create normalized value between 1-10
      const normalized = max / magnitude;
      
      // Choose appropriate interval based on normalized value
      if (normalized <= 1.5) return magnitude / 5;
      if (normalized <= 3) return magnitude / 2;
      if (normalized <= 7) return magnitude;
      return magnitude * 2;
    };
    
    const roundToNice = (value, interval) => {
      return Math.ceil(value / interval) * interval;
    };
    
    // Add 50% buffer to ensure data doesn't crowd the top of the chart
    const interval = getTickInterval(dataMax * 1.5);
    const niceMax = roundToNice(dataMax * 1.5, interval);
    
    // Generate more comprehensive ticks
    const ticks = [];
    
    // Always include 0
    ticks.push(0);
    
    // Add middle ticks
    let currentTick = interval;
    while (currentTick < niceMax) {
      ticks.push(currentTick);
      currentTick += interval;
    }
    
    // Always include the top value
    if (!ticks.includes(niceMax)) {
      ticks.push(niceMax);
    }
    
    // Sort the ticks to ensure they're in ascending order
    return ticks.sort((a, b) => a - b);
  };

  // Update the graph data when selected metric, time range, or campaign changes
  useEffect(() => {
    const newGraphData = generateData(selectedMetric, timeRange);
    setGraphData(newGraphData);
    
    // Calculate appropriate y-axis ticks based on the new data
    const updatedTicks = generateYAxisTicks(selectedMetric, newGraphData);
    setYAxisTicks(updatedTicks);
  }, [selectedMetric, timeRange, selectedCampaign]);

  // Add this number formatter helper function
  const formatNumberWithCommas = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <div className="dashboard-container">      
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <div className="workspace-label">Axiom Workspace</div>
          <h1 className="greeting">Good afternoon, John</h1>
        </div>
        
        <div className="header-controls">
          {/* Campaign Selector Dropdown */}
          <div className="campaign-dropdown">
            <select 
              value={selectedCampaign} 
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="campaign-select"
            >
              <option value="all">All Campaigns</option>
              <option value="oklahoma">Oklahoma HB 3214</option>
              <option value="cruz">Ted Cruz</option>
            </select>
          </div>
          
          {/* Time Range Dropdown */}
          {/* <div className="time-dropdown">
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
              className="time-select"
            >
              <option value="month">Monthly</option>
              <option value="week">Weekly</option>
              <option value="yesterday">Daily</option>
            </select>
          </div> */}
        </div>
      </div>
      
      {/* Metric Cards */}
      <div className="metric-cards">
        {/* Number of Calls */}
        <div 
          className={`metric-card ${selectedMetric === 'calls' ? 'selected' : ''}`}
          onClick={() => setSelectedMetric('calls')}
        >
          <div className="metric-title">{getMetricLabel('calls')}</div>
          <div className="metric-value">{getMetricValue('calls')}</div>
        </div>
        
        {/* Patch-through Calls / Voting for Cruz */}
        <div 
          className={`metric-card ${selectedMetric === 'patchthrough' ? 'selected' : ''}`}
          onClick={() => setSelectedMetric('patchthrough')}
        >
          <div className="metric-title">{getMetricLabel('patchthrough')}</div>
          <div className="metric-value">{getMetricValue('patchthrough')}</div>
        </div>
        
        {/* Persuaded Voters / Agreed */}
        <div 
          className={`metric-card ${selectedMetric === 'persuaded' ? 'selected' : ''}`}
          onClick={() => setSelectedMetric('persuaded')}
        >
          <div className="metric-title">{getMetricLabel('persuaded')}</div>
          <div className="metric-value">{getMetricValue('persuaded')}</div>
        </div>
        
        {/* AI Minutes / Call minutes */}
        <div 
          className={`metric-card ${selectedMetric === 'aiminutes' ? 'selected' : ''}`}
          onClick={() => setSelectedMetric('aiminutes')}
        >
          <div className="metric-title">{getMetricLabel('aiminutes')}</div>
          <div className="metric-value">{getMetricValue('aiminutes')}</div>
        </div>
        
        {/* SMS Messages Sent */}
        <div 
          className={`metric-card ${selectedMetric === 'sms' ? 'selected' : ''}`}
          onClick={() => setSelectedMetric('sms')}
        >
          <div className="metric-title">{getMetricLabel('sms')}</div>
          <div className="metric-value">{getMetricValue('sms')}</div>
        </div>
      </div>
      
      {/* Main Graph */}
      <div className="main-graph">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={graphData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              interval={timeRange === 'month' ? 5 : 1}
              hide={timeRange === 'yesterday'} 
            />
            <YAxis 
              tickFormatter={(value) => formatNumberWithCommas(value)} 
              domain={[0, yAxisTicks[yAxisTicks.length - 1] || 'auto']}
              ticks={yAxisTicks}
              allowDecimals={false}
            />
            <Tooltip formatter={(value) => formatNumberWithCommas(value)} />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="#9b51e0"
              activeDot={{ r: 8 }}
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Campaigns Section - Hide when specific campaign is selected */}
      {selectedCampaign === 'all' && (
        <div className="campaigns-section">
          <div className="section-header">
            <h2>Campaigns</h2>
          </div>
          
          <div className="campaigns-container">
            {/* Campaign 1 */}
            <div className="campaign-card">
              <h3 className="campaign-title">Oklahoma HB 3214</h3>
              <div className="campaign-metrics">
                <div className="campaign-metric">
                  <div className="metric-label">Number of calls</div>
                  <div className="metric-amount">9,632</div>
                </div>
                <div className="campaign-metric">
                  <div className="metric-label">Agreed</div>
                  <div className="metric-amount">1,542</div>
                </div>
                <div className="campaign-metric">
                  <div className="metric-label">Patch-through calls</div>
                  <div className="metric-amount">1,135</div>
                </div>
                <div className="campaign-metric">
                  <div className="metric-label">SMS messages sent</div>
                  <div className="metric-amount">12,850</div>
                </div>
              </div>
            </div>
            
            {/* Campaign 2 */}
            <div className="campaign-card">
              <h3 className="campaign-title">Ted Cruz</h3>
              <div className="campaign-metrics">
                <div className="campaign-metric">
                  <div className="metric-label">Number of calls</div>
                  <div className="metric-amount">218,876</div>
                </div>
                <div className="campaign-metric">
                  <div className="metric-label">Call minutes</div>
                  <div className="metric-amount">278,435</div>
                </div>
                <div className="campaign-metric">
                  <div className="metric-label">Voting for Cruz</div>
                  <div className="metric-amount">101,245</div>
                </div>
                <div className="campaign-metric">
                  <div className="metric-label">SMS messages sent</div>
                  <div className="metric-amount">143,900</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
